// Universal document editor — Sales (Quote / Invoice / Credit Note) + Purchases (Purchase Order /
// Goods Received Voucher / Supplier Credit Note). One screen drives all six via DOC_TYPES[key].
//
// Built mostly IMPERATIVELY (plain DOM + querySelector, like historynotes.js): the line grid keeps
// input focus while you type AND recomputes live totals without tearing down the row, and the
// searchSelect / <sleek-date> widgets mount into stable slots. Saving posts to the existing
// documents API (documents_service.save_document); confirming posts the ledger legs — the line's GL
// account (prefilled from the picked item's sales_account_id) is what the revenue/expense leg posts
// to, so every line must carry an account before confirm.
import { html, signal, navigate } from '../shared/t4.js';
import { api } from '../shared/api.js';
import { t } from '../shared/i18n.js';
import { tenantShell } from './tshell.js';
import { DOC_TYPES } from './documents.js';
import { searchSelect } from '../shared/searchselect.js';
import { taxCodeOption } from '../shared/formats.js';
import { icon } from '../shared/icons.js';
import { notice as toast } from '../shared/notice.js';
import { configureSleekDate } from '../shared/sleekdate.js';
import '../shared/sleekdate.js';   // registers <sleek-date>
import { attachmentsCard } from '../shared/attachments.js';
import { formatDate } from './bank_helpers.js';

// LOCAL date (not UTC) — toISOString() is UTC and can be a day behind the server's local date,
// which made the no-back-date guard misfire near midnight.
const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
// Convert chain (owner Andre 2026-09-18: "view a quote → Create Invoice → edit → confirm").
const CONVERT = {
  QUO: { type: 'INV', label: 'Create Invoice' },
  INV: { type: 'CRN', label: 'Create Credit Note' },
  PORD: { type: 'GRV', label: 'Create Goods Received Voucher' },
  GRV: { type: 'RTS', label: 'Create Supplier Credit Note', labelKey: 'doc.create_supplier_credit_note' },
};
const KEY_FOR_TYPE = Object.entries(DOC_TYPES).reduce((m, [k, v]) => { if(v.type && v.type !== 'ALL') m[v.type] = k; return m; }, {});
const money = (v, sym) => (sym || '') + Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function documentEditorScreen(companyId, docKey, docId){
  const cfg = DOC_TYPES[docKey] || {};
  const typeCode = cfg.type || 'QUO';
  const noun = cfg.nounKey ? t(cfg.nounKey, cfg.noun || cfg.label || 'Document') : (cfg.noun || cfg.label || 'Document');
  const side = cfg.group === 'purchase' ? 'purchase' : 'sales';
  const partyWord = side === 'purchase' ? t('contacts.supplier', 'Supplier') : t('contacts.customer', 'Customer');
  const noBackdate = ['QUO', 'INV', 'CRN'].includes(typeCode);
  // Only documents that post to the ledger need a GL account per line (owner Andre 2026-09-18:
  // "We don't need an account here?" on a quote). Quotes + purchase orders don't post.
  const posts = ['INV', 'CRN', 'GRV', 'RTS', 'SINV'].includes(typeCode);
  const base = '/companies/' + companyId;

  const ref = signal(null), loading = signal(true), err = signal(''), saveErr = signal('');
  let savedId = docId ? Number(docId) : null;      // set once a draft is saved
  let posted = false;                              // a confirmed/posted doc is locked (read-only)
  let currencyId = null;                            // the document's currency (contact's, else base)
  let docCur = { code: '', symbol: '' };            // …its code and mark: the totals carry THIS mark

  // ── Line models (mutated in place; the grid rebuilds only on add/remove) ──────────────────────
  // {item_id, description, qty, unit_price, discount, tax_code_id, account_id, unit}
  let lines = [];
  function blankLine(){
    const acct = posts ? (defaultAccountId() || null) : null;
    // VAT default: the default account's own tax code when it has one, else the standard 15% code.
    const tax = vatOn() ? ((acct && accountTaxCode(acct)) || standardTaxCode()) : null;
    return { item_id: null, description: '', qty: 1, unit_price: '', discount: 0, tax_code_id: tax, account_id: acct, unit: '' };
  }
  // The GL account a FREE-FORM line (no item) defaults to: the company's default SALES account for
  // a sales document (INV/QUO/CRN), default PURCHASE account for a purchase one (bill/PO/GRV/RTS).
  // Respects the client's own chart (whatever id they configured in Settings). Blank when unset.
  function defaultAccountId(){ if(!ref.value) return null; return side === 'purchase' ? (ref.value.default_purchase_account_id || null) : (ref.value.default_sales_account_id || null); }
  function accountTaxCode(accountId){ const a = (ref.value.accounts || []).find(x => Number(x.id) === Number(accountId)); return a ? (a.default_tax_code_id || null) : null; }

  function priceForCurrency(item, ccyId){
    const rows = item.prices || [];
    return rows.find(p => Number(p.currency_id) === Number(ccyId)) || null;
  }
  function taxRate(taxCodeId){
    const tc = (ref.value.tax_codes || []).find(x => Number(x.id) === Number(taxCodeId));
    return tc ? Number(tc.rate || 0) : 0;
  }
  const vatOn = () => !!(ref.value && ref.value.vat_registered);
  // The standard VAT code a new line defaults to (owner Andre 2026-09-18: "default is the 15%").
  // Sales → Output Tax (SA code "01"), Purchases → Input Tax (SA code "15"); falls back to the
  // highest positive rate on the chart.
  function standardTaxCode(){
    const codes = ref.value.tax_codes || [];
    const want = side === 'purchase' ? '15' : '01';
    const byCode = codes.find(tc => String(tc.code) === want && Number(tc.rate) > 0);
    if(byCode) return byCode.id;
    let best = null;
    for(const tc of codes){ if(Number(tc.rate) > 0 && (!best || Number(tc.rate) > Number(best.rate))) best = tc; }
    return best ? best.id : null;
  }

  // Live totals mirror the server (_compute_lines): prices are tax-EXCLUSIVE — the price is the
  // net, VAT is added on top, line total = net + VAT (100 @ 15% → 115).
  function lineNet(ln){
    const qty = Number(ln.qty) || 0, price = Number(ln.unit_price) || 0;
    const disc = Math.min(100, Math.max(0, Number(ln.discount) || 0));
    return Math.round(qty * price * (1 - disc / 100) * 100) / 100;
  }
  function lineGross(ln){
    const net = lineNet(ln);
    const rate = (vatOn() && ln.tax_code_id) ? taxRate(ln.tax_code_id) : 0;
    const tax = rate ? Math.round(net * rate / 100 * 100) / 100 : 0;
    return Math.round((net + tax) * 100) / 100;
  }
  function totals(){
    let sub = 0, vat = 0, tot = 0;
    for(const ln of lines){
      const net = lineNet(ln);
      const rate = (vatOn() && ln.tax_code_id) ? taxRate(ln.tax_code_id) : 0;
      const tax = rate ? Math.round(net * rate / 100 * 100) / 100 : 0;
      sub += net; vat += tax; tot += net + tax;
    }
    return { sub: Math.round(sub * 100) / 100, vat: Math.round(vat * 100) / 100, tot: Math.round(tot * 100) / 100 };
  }

  // ── Static shell (built once; async load populates it) ────────────────────────────────────────
  // Capture the ROOT ELEMENT (not the fragment html`` returns) — the fragment is emptied once
  // tenantShell mounts it, so imperative node.querySelector must run against the element that stays
  // in the DOM.
  const _frag = html`
    <div class="page page-wide ct-page de-page">
      <button class="btn btn-sm btn-link ct-back" @click=${back}>${icon('chevron-left', {size: 14})} ${cfg.group === 'purchase' ? t('nav.purchases_v2', 'Purchases') : t('nav.sales_v2', 'Sales')}</button>
      <div class="de-head">
        <h1 class="de-title">${docId ? t('doc.edit', 'Edit') : t('doc.new', 'New')} ${noun}</h1>
        <div class="de-head-actions" data-head-actions></div>
      </div>
      ${() => err.value ? html`<div class="row-err mb-2">${err.value}</div>` : ''}
      ${() => loading.value ? html`<div class="muted-block">${t('common.loading', 'Loading…')}</div>` : ''}
      <div data-editor hidden>
        <div class="card de-card">
          <div class="de-grid">
            <label class="ct-field de-party"><span class="jrn-lbl">${partyWord}</span>
              <span data-party></span></label>
            <div class="de-addparty" data-addparty-panel hidden></div>
            <label class="ct-field de-datef" data-adv hidden><span class="jrn-lbl">${t('doc.date', 'Date')}</span>
              <sleek-date data-doc-date data-free="true"></sleek-date></label>
            <label class="ct-field de-datef" data-adv hidden><span class="jrn-lbl">${side === 'purchase' ? t('doc.expected', 'Expected date') : t('doc.valid_until', 'Valid until')}</span>
              <sleek-date data-due-date data-free="true"></sleek-date></label>
            <label class="ct-field" data-adv hidden><span class="jrn-lbl">${t('doc.currency', 'Currency')}</span>
              <span data-ccy class="de-ccy"></span></label>
            <label class="ct-field de-ref"><span class="jrn-lbl">${t('doc.source_ref', 'Source document / reference')}</span>
              <input class="form-control" data-ref placeholder=${t('doc.source_ref_eg', 'e.g. their PO number')}></label>
          </div>
          <small class="cur-hint" data-ccy-hint data-draft-only hidden></small>
          <div class="de-coop-warn" data-coop-warn hidden></div>
        </div>

        <div class="card de-card">
          <div class="de-lines-head">
            <div>${t('doc.lines', 'Lines')}</div>
            <div class="muted de-lines-hint">${t('doc.lines_hint', 'Pick a service item — or add a new one — set qty and discount.')}</div>
          </div>
          <div class="de-lines-wrap"><table class="de-lines"><thead><tr>
            <th class="de-c-item">${t('doc.item', 'Item / description')}</th>
            <th class="de-c-qty ta-r">${t('doc.qty', 'Qty')}</th>
            <th class="de-c-price ta-r">${t('doc.price', 'Price')}</th>
            <th class="de-c-disc ta-r">${t('doc.disc', 'Disc %')}</th>
            <th class="de-c-vat" data-vat-col>${t('doc.vat', 'VAT')}</th>
            <th class="de-c-acct" data-acct-col>${t('doc.account', 'GL account')}</th>
            <th class="de-c-total ta-r">${t('doc.total', 'Total')}</th>
            <th class="de-c-x"></th>
          </tr></thead><tbody data-lines></tbody></table></div>
          <button type="button" class="btn btn-sm btn-outline de-addline" data-add-line>${icon('plus', {size: 13})} ${t('doc.add_line', 'Add line')}</button>
        </div>

        <div class="de-foot">
          <div class="de-notes card de-card">
            <label class="ct-field"><span class="jrn-lbl">${t('doc.notes', 'Notes')}</span>
              <textarea class="form-control" rows="3" data-notes placeholder=${t('doc.notes_eg', 'Notes shown on the document…')}></textarea></label>
          </div>
          <div class="de-totals card de-card">
            <div class="de-tot-row"><span>${t('doc.subtotal', 'Subtotal')}</span><b data-subtotal>—</b></div>
            <div class="de-tot-row" data-vat-row><span>${t('doc.vat', 'VAT')}</span><b data-vat>—</b></div>
            <div class="de-tot-row de-tot-grand"><span>${t('doc.total', 'Total')}</span><b data-grand>—</b></div>
          </div>
        </div>

        <div data-recurring-slot data-draft-only></div>

        <div class="de-attach card de-card" data-attach-slot>
          <div class="de-attach-head">${t('doc.attachments', 'Attachments')}</div>
          <div class="muted de-attach-hint" data-attach-hint>${t('doc.attach_save_first', 'Save the document, then drag files here or browse.')}</div>
        </div>

        <div class="de-actions">
          ${() => saveErr.value ? html`<div class="row-err">${saveErr.value}</div>` : ''}
          <span class="jrn-spacer"></span>
          <button type="button" class="btn btn-outline" @click=${back}>${t('common.cancel', 'Cancel')}</button>
          <label class="switch de-send-toggle" data-draft-only><input type="checkbox" data-send-toggle><span class="switch-track"></span> ${t('doc.email_on_confirm', 'Email to contact')}</label>
          <button type="button" class="btn btn-outline" data-save data-draft-only>${t('common.save', 'Save')}</button>
          <button type="button" class="btn btn-primary" data-confirm data-draft-only>${t('doc.confirm_post', 'Confirm')}</button>
          <button type="button" class="btn btn-primary" data-send hidden>${t('doc.send', 'Send')}</button>
        </div>
      </div>
    </div>`;
  const node = _frag.firstElementChild || _frag;

  function back(){ navigate('/co/' + companyId + '/' + docKey); }

  // ── Line grid (imperative) ────────────────────────────────────────────────────────────────────
  function recompute(){
    const { sub, vat, tot } = totals();
    // the DOCUMENT's currency mark — a dollar invoice's totals are dollars, whatever the company's base
    const sym = docCur.symbol || (docCur.code ? docCur.code + ' ' : '');
    node.querySelector('[data-subtotal]').textContent = money(sub, sym);
    node.querySelector('[data-vat]').textContent = money(vat, sym);
    node.querySelector('[data-grand]').textContent = money(tot, sym);
    // per-line totals
    lines.forEach((ln, i) => {
      const row = node.querySelector(`[data-line="${i}"]`);
      if(!row) return;
      const cell = row.querySelector('[data-total]');
      // Don't overwrite the total while the user is editing it (reverse-calc mode).
      if(cell && document.activeElement !== cell) cell.value = lineGross(ln).toFixed(2);
    });
  }

  function renderLines(){
    const host = node.querySelector('[data-lines]');
    host.replaceChildren();
    lines.forEach((ln, i) => host.appendChild(lineRow(ln, i)));
    recompute();
  }

  function lineRow(ln, idx){
    const vat = vatOn();
    const frag = html`<tr data-line=${String(idx)}>
      <td class="de-c-item" data-label=${t('doc.item', 'Item')}><div class="de-item-wrap"><span data-item-pick></span>
        <input class="form-control de-desc" data-desc placeholder=${t('doc.desc_eg', 'Description')}></div></td>
      <td class="de-c-qty" data-label=${t('doc.qty', 'Qty')}><input class="form-control text-right" type="text" inputmode="decimal" data-qty></td>
      <td class="de-c-price" data-label=${t('doc.price', 'Price')}><input class="form-control text-right" type="text" inputmode="decimal" data-price></td>
      <td class="de-c-disc" data-label=${t('doc.disc', 'Disc %')}><input class="form-control text-right" type="text" inputmode="decimal" data-disc></td>
      <td class="de-c-vat" data-label=${t('doc.vat', 'VAT')}><span data-tax-pick></span></td>
      <td class="de-c-acct" data-label=${t('doc.account', 'GL account')}><span data-acct-pick></span></td>
      <td class="de-c-total" data-label=${t('doc.total', 'Total')}><input class="form-control text-right" type="text" inputmode="decimal" data-total></td>
      <td class="de-c-x"><button type="button" class="btn-row-del" data-del title=${t('common.remove', 'Remove')}>${icon('trash-2', {size: 14})}</button></td>
    </tr>`;
    const row = frag.querySelector('tr');
    if(!vat) row.querySelector('.de-c-vat').setAttribute('hidden', '');
    // GL account column: shown for POSTING documents (INV/CRN/GRV/RTS/SINV) so a FREE-FORM line can
    // target an account the user picks. An item line prefills its account from the item but stays
    // overridable here. Quotes / purchase orders don't post → the column stays hidden.
    if(!posts) row.querySelector('.de-c-acct').setAttribute('hidden', '');
    let taxSel = null, acctSel = null;
    const desc = row.querySelector('[data-desc]'); desc.value = ln.description || '';
    const qty = row.querySelector('[data-qty]'); qty.value = ln.qty ?? '';
    const price = row.querySelector('[data-price]'); price.value = ln.unit_price ?? '';
    const disc = row.querySelector('[data-disc]'); disc.value = ln.discount ?? '';
    const totalIn = row.querySelector('[data-total]');
    desc.addEventListener('input', () => { ln.description = desc.value; });
    qty.addEventListener('input', () => { ln.qty = qty.value; recompute(); });
    price.addEventListener('input', () => { ln.unit_price = price.value; recompute(); });
    disc.addEventListener('input', () => { ln.discount = disc.value; recompute(); });
    // Editable total → back-calculate the net unit price (owner Andre 2026-09-18: "Total should be
    // editable so reverse can be worked out"). gross = net*(1+rate) → net = gross/(1+rate).
    totalIn.addEventListener('input', () => {
      const gross = Number(totalIn.value) || 0;
      const rate = (vatOn() && ln.tax_code_id) ? taxRate(ln.tax_code_id) : 0;
      const disc2 = Math.min(100, Math.max(0, Number(ln.discount) || 0));
      const qv = Number(ln.qty) || 0;
      const net = gross / (1 + rate / 100);
      const unit = (qv > 0 && disc2 < 100) ? net / (qv * (1 - disc2 / 100)) : 0;
      ln.unit_price = Math.round(unit * 100) / 100;
      price.value = ln.unit_price;
      recompute();   // updates the footer + other lines; skips this focused total input
    });
    row.querySelector('[data-del]').addEventListener('click', () => {
      lines.splice(idx, 1);
      if(!lines.length) lines.push(blankLine());
      renderLines();
    });

    // Item picker — pick prefills description/price/unit/tax/account for the doc currency.
    const itemOpts = (ref.value.items || []).map(it => ({ value: String(it.id), label: it.code + ' — ' + it.name, keywords: it.name }));
    const itemSel = searchSelect({ options: itemOpts, value: ln.item_id ? String(ln.item_id) : '',
      placeholder: t('doc.search_items', 'Search service items…'), ariaLabel: t('doc.item', 'Item'),
      onSelect: v => {
        ln.item_id = v ? Number(v) : null;
        const it = (ref.value.items || []).find(x => Number(x.id) === Number(v));
        if(it){
          if(!ln.description) { ln.description = it.name; desc.value = it.name; }
          ln.account_id = it.sales_account_id || null;   // ledger account prefilled from the item
          acctSel && acctSel.set(ln.account_id ? String(ln.account_id) : '');   // reflect it in the picker
          const p = priceForCurrency(it, currencyId);
          if(p){
            ln.unit_price = p.rate != null ? String(p.rate) : ''; price.value = ln.unit_price;
            ln.unit = p.unit || '';
            if(vat && p.sales_tax_code_id){ ln.tax_code_id = p.sales_tax_code_id; taxSel && taxSel.set(String(p.sales_tax_code_id)); }
          } else {
            price.value = ''; ln.unit_price = '';
            toast(t('doc.no_price_ccy', 'This item has no price in the document currency'), 'warn');
          }
          recompute();
        }
      } });
    row.querySelector('[data-item-pick]').append(itemSel.el);

    // VAT rate select (only when the company is VAT registered — e.g. SA).
    if(vat){
      // Show the RATE (e.g. "15%") in the collapsed box (owner Andre 2026-09-18: "important is to
      // show the 15% not the type") — but a rated label is honest only while the rate identifies the
      // code. Several codes can share a rate (Finland: five at 0%), so taxCodeOption() leads those
      // with their NAME and keeps the real rate ("25.5%", never a rounded "26%"). minWidth widens the
      // popup beyond the ~92px VAT column so the long Finnish names aren't clipped.
      const taxOpts = (ref.value.tax_codes || []).map(taxCodeOption);
      taxSel = searchSelect({ options: taxOpts, value: ln.tax_code_id ? String(ln.tax_code_id) : '',
        ariaLabel: t('doc.vat', 'VAT'), placeholder: t('doc.vat', 'VAT'), minWidth: 300,
        onSelect: v => { ln.tax_code_id = v ? Number(v) : null; recompute(); } });
      row.querySelector('[data-tax-pick]').append(taxSel.el);
    }

    // Per-line GL account picker (posting documents only). Lets a FREE-FORM line target any postable
    // account; an item line shows the item's account, still overridable. Picking an account defaults
    // the line's VAT to that account's default tax code (owner-deferred → ~2-click ad-hoc lines).
    if(posts){
      const acctOpts = (ref.value.accounts || []).map(a => ({ value: String(a.id), label: a.code + ' — ' + a.name, keywords: a.name }));
      acctSel = searchSelect({ options: acctOpts, value: ln.account_id ? String(ln.account_id) : '',
        ariaLabel: t('doc.account', 'GL account'), placeholder: t('doc.pick_account', 'Pick account…'),
        onSelect: v => {
          ln.account_id = v ? Number(v) : null;
          // Default the line's VAT from the chosen account's default tax code (overridable).
          if(vat && ln.account_id){
            const tcid = accountTaxCode(ln.account_id);
            if(tcid){ ln.tax_code_id = tcid; taxSel && taxSel.set(String(tcid)); }
          }
          recompute();
        } });
      row.querySelector('[data-acct-pick]').append(acctSel.el);
    }
    return row;
  }

  // ── Party (contact) picker + inline add ───────────────────────────────────────────────────────
  let partySel = null;
  function partyOptions(){
    const flag = side === 'purchase' ? 'is_supplier' : 'is_customer';
    return (ref.value.contacts || []).filter(c => c[flag]).map(c => ({ value: String(c.id), label: c.name, sub: c.code, keywords: c.code + ' ' + (c.email || '') }));
  }
  // The document's currency → { code, symbol } (owner 2026-10-19: every figure in its own currency):
  // the company's currency list first, then what the contact or the stored document says about it,
  // then the base — never the base's code for another currency.
  function currencyInfo(id, hint){
    const r = ref.value || {};
    const c = (r.currencies || []).find(x => Number(x.id) === Number(id));
    if(c) return { code: c.code || '', symbol: c.symbol || '' };
    if(hint && (hint.code || hint.symbol)) return { code: hint.code || '', symbol: hint.symbol || '' };
    if(id === null || id === undefined || Number(id) === Number(r.base_currency_id))
      return { code: r.currency_code || '', symbol: r.currency_symbol || '' };
    return { code: '', symbol: '' };
  }
  function setCurrency(id, hint){
    const r = ref.value || {};
    currencyId = id;
    docCur = currencyInfo(id, hint);
    node.querySelector('[data-ccy]').textContent = [docCur.code, docCur.symbol].filter(Boolean).join(' · ');
    // A document in another currency than the company's is converted to the company's currency, at
    // the exchange rate of the document date, when it posts. Say so on every document that posts.
    const foreign = id !== null && id !== undefined && r.base_currency_id !== null && r.base_currency_id !== undefined
      && Number(id) !== Number(r.base_currency_id);
    const hintEl = node.querySelector('[data-ccy-hint]');
    if(hintEl){
      hintEl.hidden = !(foreign && posts);
      hintEl.textContent = foreign && posts
        ? t('doc.currency_converts', 'This document is in {code}. When it is posted, its amounts are converted to {base} at the exchange rate of the document date.')
          .replace('{code}', docCur.code || docCur.symbol || '?').replace('{base}', r.currency_code || t('doc.company_currency', 'your company’s currency'))
        : '';
    }
  }
  // A purchase document on a co-op's supplier contact, dated from the co-op's start (its earliest imported statement's
  // first day) on: the co-op's purchases are booked from its statements, so one captured here as well is in the books
  // twice (Fable's final check, finding 5; 2026-10-26) — a warning, never a block. A draft or a new document only.
  let coopOf = {};                                  // supplier contact id → { name, start } of the co-op it is
  const coopsByContact = r => ((r && r.data) || []).reduce((m, c) => {
    if(!c.contact_id || !c.start) return m;
    const k = String(c.contact_id), was = m[k];
    if(!was || c.start < was.start) m[k] = { name: c.name || '', start: c.start };   // two co-ops on one contact: the earlier
    return m;
  }, {});
  function coopWarn(){
    const el = node.querySelector('[data-coop-warn]');
    const c = !posted && partySel ? coopOf[partySel.get()] : null;
    const on = String(node.querySelector('[data-doc-date]').value || todayIso()).slice(0, 10);
    const show = !!(c && on >= c.start);
    el.hidden = !show;
    el.textContent = show ? '⚠ ' + t('doc.coop_twice', '{coop}’s statements are imported from {start}, and what is on them is booked from them. Capture this document only if it is not on {coop}’s statement — otherwise it is in the books twice.')
      .split('{coop}').join(c.name).replace('{start}', formatDate(c.start, (ref.value || {}).date_format)) : '';
  }
  function selectParty(id){
    const c = (ref.value.contacts || []).find(x => Number(x.id) === Number(id));
    setCurrency((c && c.preferred_currency_id) || ref.value.base_currency_id,
      c ? { code: c.currency_code, symbol: c.currency_symbol } : null);
    // Re-price existing item lines for the new currency.
    lines.forEach(ln => {
      if(!ln.item_id) return;
      const it = (ref.value.items || []).find(x => Number(x.id) === Number(ln.item_id));
      const p = it && priceForCurrency(it, currencyId);
      if(p && (ln.unit_price === '' || ln.unit_price == null)) ln.unit_price = String(p.rate);
    });
    renderLines();
    coopWarn();
  }

  // Open the inline add-party panel, prefilled with the name the user typed into the picker (the
  // searchSelect's onCreate fires this when nothing matched — owner Andre 2026-09-18: "no New
  // button … the Nothing found in drop down should allow me to add the needed details").
  function openAddParty(prefillName){
    const panel = node.querySelector('[data-addparty-panel]');
    {
      panel.innerHTML = '';
      const p = html`<div class="de-addparty-form">
        <div class="de-addparty-head">${t('doc.add_party', 'Add')} ${partyWord.toLowerCase()}</div>
        <div class="de-grid">
          <label class="ct-field"><span class="jrn-lbl">${t('common.name', 'Name')}</span><input class="form-control" data-np-name placeholder=${t('contacts.eg_name', 'e.g. Blue Widgets (Pty) Ltd')}></label>
          <label class="ct-field"><span class="jrn-lbl">${t('common.email', 'Email')}</span><input class="form-control" data-np-email type="email" placeholder=${t('contacts.eg_email', 'e.g. pay@blue.co.za')}></label>
          <label class="ct-field"><span class="jrn-lbl">${t('contacts.phone', 'Phone')}</span><input class="form-control" data-np-phone placeholder=${t('contacts.eg_phone', 'e.g. 011 000 0000')}></label>
        </div>
        <label class="switch ct-onboard-toggle">
          <input type="checkbox" data-np-onboard checked>
          <span class="switch-track"></span>
          <span>${t('contacts.send_onboarding', 'Send onboarding link')}</span>
          <small class="muted">${t('contacts.send_onboarding_sub', 'They get a link to their portal (needs an email).')}</small>
        </label>
        <div class="de-addparty-actions">
          <span class="row-err" data-np-err></span>
          <button type="button" class="btn btn-sm btn-outline" data-np-cancel>${t('common.cancel', 'Cancel')}</button>
          <button type="button" class="btn btn-sm btn-primary" data-np-save>${t('common.save', 'Save')}</button>
        </div></div>`;
      panel.append(p);
      panel.removeAttribute('hidden');
      // Query from `panel` (the stable container) — html`` returns a fragment that is emptied once
      // appended, so p.querySelector would return null and no listeners would attach.
      const nameEl = panel.querySelector('[data-np-name]'), emailEl = panel.querySelector('[data-np-email]');
      // Prefill with the typed name; fall back to the picker's live input if onCreate passed nothing.
      const nm = (prefillName || (partySel && partySel.input && partySel.input.value) || '').trim();
      nameEl.value = nm;
      (nm ? emailEl : nameEl).focus();
      const save = panel.querySelector('[data-np-save]'), errEl = panel.querySelector('[data-np-err]');
      panel.querySelector('[data-np-cancel]').addEventListener('click', () => { panel.innerHTML = ''; panel.setAttribute('hidden', ''); });
      save.addEventListener('click', async () => {
        const name = nameEl.value.trim();
        if(!name){ errEl.textContent = t('contacts.name_required', 'A name is required.'); return; }
        save.disabled = true;
        const email = emailEl.value.trim();
        const wantOnboard = panel.querySelector('[data-np-onboard]').checked;
        try {
          const res = await api.post(base + '/contacts', { name, type: side === 'purchase' ? 1 : 0,
            email: email || undefined,
            phone: panel.querySelector('[data-np-phone]').value.trim() || undefined,
            send_onboarding: !!(wantOnboard && email) });
          // Refresh reference contacts, then select the new one.
          const fresh = await api.get(base + '/documents-reference');
          ref.value = fresh;
          partySel.setOptions(partyOptions());
          partySel.set(String(res.id));
          selectParty(res.id);
          panel.setAttribute('hidden', '');
          toast(t('doc.party_added', 'Added'));
        } catch(e){ errEl.textContent = (e.data && e.data.error) || t('contacts.create_failed', 'Could not create the contact'); save.disabled = false; }
      });
    }
  }

  // ── Save / confirm ────────────────────────────────────────────────────────────────────────────
  function collect(){
    const clean = lines.filter(ln => (Number(ln.qty) || 0) > 0 && (ln.description || ln.item_id));
    return {
      type_code: typeCode,
      contact_id: partySel.get() ? Number(partySel.get()) : null,
      document_date: node.querySelector('[data-doc-date]').value || todayIso(),
      due_date: node.querySelector('[data-due-date]').value || null,
      reference: node.querySelector('[data-ref]').value.trim() || null,
      notes: node.querySelector('[data-notes]').value.trim() || null,
      currency_id: currencyId,
      lines: clean.map(ln => ({
        description: (ln.description || '').trim(),
        qty: Number(ln.qty) || 0,
        unit_price: Number(ln.unit_price) || 0,
        discount: Math.min(100, Math.max(0, Number(ln.discount) || 0)),
        tax_code_id: vatOn() ? (ln.tax_code_id || null) : null,
        account_id: ln.account_id || null,
        item_id: ln.item_id || null,
      })),
    };
  }
  async function doSave(){
    saveErr.value = '';
    const body = collect();
    if(!body.contact_id){ saveErr.value = t('doc.need_party', 'Choose a ' + partyWord.toLowerCase()); return null; }
    if(!body.lines.length){ saveErr.value = t('doc.need_line', 'Add at least one line'); return null; }
    const btn = node.querySelector('[data-save]'); btn.disabled = true; btn.textContent = t('common.saving', 'Saving…');
    try {
      const res = savedId
        ? await api.put(base + '/documents/' + savedId, body)
        : await api.post(base + '/documents', body);
      savedId = res.id || savedId;
      toast(t('doc.saved', 'Draft saved'));
      node.querySelector('[data-confirm]').hidden = false;
      await uploadPending();      // push any files added before the first save
      mountAttachments();
      mountRowActions();          // Clone / Repeat become available once there's a saved id
      await syncRecurring();      // create / update / remove the recurring profile from the toggle
      return res;
    } catch(e){ saveErr.value = (e.data && e.data.error) || t('doc.save_failed', 'Could not save the document'); return null; }
    finally { btn.disabled = false; btn.textContent = t('common.save', 'Save'); }
  }
  const listKey = () => (cfg.group === 'purchase' ? 'all-purchases' : 'all-sales');
  async function doConfirm(){
    const alsoSend = !!node.querySelector('[data-send-toggle]')?.checked;
    // Always persist the current edits FIRST — otherwise confirming an existing draft would post
    // the stored version and drop in-editor changes (owner Andre 2026-09-18: a GRV kept the PO's
    // original qty because Confirm skipped the save).
    const r = await doSave();
    if(!r) return;
    const btn = node.querySelector('[data-confirm]'); btn.disabled = true;
    try {
      await api.post(base + '/documents/' + savedId + '/confirm', {});
      if(alsoSend){
        try { await api.post(base + '/documents/' + savedId + '/send', {}); toast(t('doc.confirmed_sent', 'Confirmed & sent')); }
        catch(se){ toast((se.data && se.data.error) || t('doc.send_failed', 'Confirmed, but could not send'), 'warn'); }
      } else {
        toast(t('doc.confirmed', 'Confirmed'));
      }
      navigate('/co/' + companyId + '/' + listKey());   // land on the list so the doc is visible
    } catch(e){ saveErr.value = (e.data && e.data.error) || t('doc.confirm_failed', 'Could not confirm'); btn.disabled = false; }
  }

  // ── Clone / Repeat ──────────────────────────────────────────────────────────────────────────
  // CLONE copies the document to a new same-type draft keeping its dates; REPEAT ('bill again')
  // re-dates the copy to today. Both open the new draft in this editor. Available on any saved doc.
  const REPEAT_TYPES = ['INV', 'SINV', 'QUO', 'PORD'];   // invoices, bills, and orders
  async function doClone(redate){
    // A draft may hold unsaved edits — persist them first so the clone copies what's on screen.
    if(!posted){ const r = await doSave(); if(!r) return; }
    if(!savedId) return;
    try {
      const res = await api.post(base + '/documents/' + savedId + '/clone', { redate: !!redate });
      const key = KEY_FOR_TYPE[res.type_code] || docKey;
      toast(redate ? t('doc.repeated', 'Repeated — new draft opened') : t('doc.cloned', 'Cloned — new draft opened'));
      navigate('/co/' + companyId + '/' + key + '/' + res.id + '/edit');
    } catch(e){ saveErr.value = (e.data && e.data.error) || t('doc.clone_failed', 'Could not clone'); }
  }
  function mountRowActions(){
    if(!savedId) return;
    const host = node.querySelector('[data-head-actions]');
    if(!host || host.dataset.rowActions) return;
    host.dataset.rowActions = '1';
    const cloneBtn = html`<button type="button" class="btn btn-sm btn-outline">${t('doc.clone', 'Clone')}</button>`.firstElementChild;
    cloneBtn.addEventListener('click', () => doClone(false));
    host.append(cloneBtn);
    if(REPEAT_TYPES.includes(typeCode)){
      const repBtn = html`<button type="button" class="btn btn-sm btn-outline">${t('doc.repeat', 'Repeat')}</button>`.firstElementChild;
      repBtn.addEventListener('click', () => doClone(true));
      host.append(repBtn);
    }
  }

  // ── Make recurring (invoices + bills only — the recurring engine fires INV/SINV) ──────────────
  const recurringEligible = ['INV', 'SINV'].includes(typeCode);
  let recurringProfileId = null;
  function mountRecurring(){
    if(!recurringEligible) return;
    const slot = node.querySelector('[data-recurring-slot]');
    if(!slot || slot.dataset.mounted) return;
    slot.dataset.mounted = '1';
    const frag = html`<div class="card de-card de-recurring">
      <label class="switch de-rec-toggle"><input type="checkbox" data-rec-on><span class="switch-track"></span> ${t('doc.make_recurring', 'Make recurring')}</label>
      <div class="muted de-rec-hint">${t('doc.make_recurring_hint', 'Raise this document automatically on a schedule.')}</div>
      <div class="de-rec-fields" data-rec-details hidden>
        <div class="de-grid">
          <label class="ct-field"><span class="jrn-lbl">${t('doc.rec_every', 'Every')}</span>
            <input class="form-control text-right" type="number" min="1" step="1" value="1" data-rec-every></label>
          <label class="ct-field"><span class="jrn-lbl">${t('doc.rec_unit', 'Period')}</span>
            <select class="form-control" data-rec-unit>
              <option value="month">${t('doc.rec_month', 'Month(s)')}</option>
              <option value="week">${t('doc.rec_week', 'Week(s)')}</option>
            </select></label>
          <label class="ct-field"><span class="jrn-lbl">${t('doc.rec_start', 'Start date')}</span>
            <sleek-date data-rec-start data-free="true" class="form-control"></sleek-date></label>
          <label class="ct-field"><span class="jrn-lbl">${t('doc.rec_until', 'Until (blank = forever)')}</span>
            <sleek-date data-rec-until data-free="true" class="form-control"></sleek-date></label>
        </div>
        <label class="switch de-rec-auto"><input type="checkbox" data-rec-auto><span class="switch-track"></span> ${t('doc.rec_autoconfirm', 'Auto-confirm & email each run')}</label>
        <div class="muted de-rec-auto-hint">${t('doc.rec_autoconfirm_hint', 'On: each run posts the document and emails the contact. Off: each run lands in Drafts to confirm.')}</div>
      </div>
    </div>`;
    slot.replaceChildren(frag.firstElementChild || frag);
    const on = slot.querySelector('[data-rec-on]'), details = slot.querySelector('[data-rec-details]');
    slot.querySelector('[data-rec-start]').value = todayIso();
    on.addEventListener('change', () => { details.hidden = !on.checked; });
  }
  async function syncRecurring(){
    if(!recurringEligible) return;
    const on = node.querySelector('[data-rec-on]');
    if(!on) return;
    try {
      // A recurring schedule raises its documents in the company's own currency, so a schedule made
      // from a document in another currency would bill its figures as the company's currency.
      const r = ref.value || {};
      if(on.checked && currencyId !== null && currencyId !== undefined && r.base_currency_id !== null
         && r.base_currency_id !== undefined && Number(currencyId) !== Number(r.base_currency_id)){
        toast(t('doc.recurring_base_only', 'A recurring schedule can only be made for a document in your company’s currency ({base}).')
          .replace('{base}', r.currency_code || ''), 'warn');
        return;
      }
      if(on.checked){
        const body = {
          schedule_every: Number(node.querySelector('[data-rec-every]').value) || 1,
          schedule_unit: node.querySelector('[data-rec-unit]').value || 'month',
          start_date: node.querySelector('[data-rec-start]').value || todayIso(),
          end_date: node.querySelector('[data-rec-until]').value || null,
          action: node.querySelector('[data-rec-auto]').checked ? 'confirm_send' : 'draft',
          allow_draft: true,
        };
        // At-creation flow: recreate so the profile reflects the latest saved lines (no fired
        // history yet). The document is already saved, so make-recurring seeds from it.
        if(recurringProfileId){ try { await api.del(base + '/recurring-profiles/' + recurringProfileId); } catch { /* ignore */ } recurringProfileId = null; }
        const res = await api.post(base + '/documents/' + savedId + '/make-recurring', body);
        recurringProfileId = res.id || null;
        toast(t('doc.recurring_saved', 'Recurring schedule saved'));
      } else if(recurringProfileId){
        try { await api.del(base + '/recurring-profiles/' + recurringProfileId); } catch { /* ignore */ }
        recurringProfileId = null;
        toast(t('doc.recurring_removed', 'Recurring schedule removed'));
      }
    } catch(e){
      toast((e.data && e.data.error) || t('doc.recurring_failed', 'Could not save the recurring schedule'), 'warn');
    }
  }

  // Attachments can be added BEFORE the document is saved (owner Andre 2026-09-18): files are held
  // client-side in `pendingFiles` and uploaded to the document once it is first saved, after which
  // the real attachmentsCard takes over.
  const pendingFiles = [];
  const fileToB64 = f => new Promise(res => { const r = new FileReader(); r.onload = () => res(String(r.result).split(',')[1] || ''); r.readAsDataURL(f); });
  async function uploadPending(){
    for(const pf of pendingFiles.slice()){
      try { await api.post(base + '/documents/' + savedId + '/attachments', pf); } catch { /* keep going */ }
    }
    pendingFiles.length = 0;
  }
  function renderPendingList(){
    const list = node.querySelector('[data-attach-list]');
    if(!list) return;
    list.replaceChildren();
    pendingFiles.forEach((pf, i) => {
      const row = html`<div class="de-attach-row"><span>${pf.filename}</span><button type="button" class="btn-link danger" data-rm>${t('common.remove', 'Remove')}</button></div>`;
      const el = row.firstElementChild || row;
      el.querySelector('[data-rm]').addEventListener('click', () => { pendingFiles.splice(i, 1); renderPendingList(); });
      list.append(el);
    });
  }
  function mountPendingAttachments(){
    const slot = node.querySelector('[data-attach-slot]');
    slot.replaceChildren();
    const frag = html`<div>
      <div class="de-attach-head">${t('doc.attachments', 'Attachments')}</div>
      <label class="btn btn-sm btn-outline de-attach-btn">${t('doc.choose_files', 'Choose files')}<input type="file" multiple hidden data-attach-input></label>
      <div class="de-attach-list" data-attach-list></div>
      <div class="muted de-attach-hint">${t('doc.attach_pending_hint', 'Files attach automatically when you save the document.')}</div>
    </div>`;
    slot.append(frag.firstElementChild || frag);
    slot.querySelector('[data-attach-input]').addEventListener('change', async e => {
      for(const f of e.target.files){ pendingFiles.push({ filename: f.name, mime: f.type, content_base64: await fileToB64(f) }); }
      e.target.value = '';
      renderPendingList();
    });
    renderPendingList();
  }
  function mountAttachments(){
    if(!savedId) return;
    const slot = node.querySelector('[data-attach-slot]');
    if(slot.dataset.mounted) return;
    slot.dataset.mounted = '1';
    slot.replaceChildren();
    // attachmentsCard returns a view FUNCTION that self-loads and re-renders via its own signals;
    // render it through a reactive slot (${() => view()}) so it updates when the list loads.
    const view = attachmentsCard({ base: base + '/documents/' + savedId,
      attachmentBase: base + '/attachments', title: t('doc.attachments', 'Attachments') });
    slot.append(html`${() => view()}`);
  }

  // ── Boot ──────────────────────────────────────────────────────────────────────────────────────
  (async () => {
    try {
      // a purchase document that posts: the company's co-ops (GET /coops) — is its supplier one, and since when. Never
      // waited on: the warning shows once the read is in (coopWarn)
      if(posts && side === 'purchase')
        api.get(base + '/coops').catch(() => null).then(r => { coopOf = coopsByContact(r); coopWarn(); });
      ref.value = await api.get(base + '/documents-reference');
      configureSleekDate({ date_format: ref.value.date_format, periods: ref.value.periods,
        fy_start_day: ref.value.fy_start_day, fy_start_month: ref.value.fy_start_month });
      setCurrency(ref.value.base_currency_id);

      // VAT column visibility; the GL-account column shows only for posting documents (a per-line
      // picker for free-form lines) and stays hidden on quotes / purchase orders.
      if(!vatOn()){
        node.querySelector('[data-vat-col]')?.setAttribute('hidden', '');
        node.querySelector('[data-vat-row]')?.setAttribute('hidden', '');
      }
      if(!posts) node.querySelector('[data-acct-col]')?.setAttribute('hidden', '');

      // Party picker.
      partySel = searchSelect({ options: partyOptions(), value: '',
        placeholder: t('doc.search_party', 'Search ' + partyWord.toLowerCase() + '…'), ariaLabel: partyWord,
        createLabel: '+ ' + t('doc.add_named', "Add '{q}'"),
        onCreate: typed => openAddParty(typed),
        onSelect: v => { if(v) selectParty(v); } });
      node.querySelector('[data-party]').append(partySel.el);

      // Dates default so the user never has to fill them (owner Andre 2026-09-18: "I really don't
      // want to be filling in ISSUED / VALID UNTIL"). Issued = today (sales docs can't be back-dated
      // → calendar min is today); valid-until / expected = today + 30 days.
      const dd = node.querySelector('[data-doc-date]');
      if(noBackdate) dd.dataset.min = todayIso();
      dd.value = todayIso();
      dd.addEventListener('change', coopWarn);      // the co-op warning follows the date chosen (a draft's is fixed)
      // Valid-until / due date = today + the company's configured term (Settings): a quote uses
      // quote_valid_days, everything else uses invoice_due_days. Both default to 30.
      const termDays = typeCode === 'QUO'
        ? Number(ref.value.quote_valid_days ?? 30)
        : Number(ref.value.invoice_due_days ?? 30);
      const due = new Date(); due.setDate(due.getDate() + (Number.isFinite(termDays) ? termDays : 30));
      node.querySelector('[data-due-date]').value = `${due.getFullYear()}-${String(due.getMonth() + 1).padStart(2, '0')}-${String(due.getDate()).padStart(2, '0')}`;

      // Existing draft?
      if(savedId){
        // Load the existing document, then populate the form.
        const doc = await api.get(base + '/documents/' + savedId);
        const d = doc.document || doc;
        const docLines = doc.lines || d.lines || d.details || [];
        // Reveal the advanced fields (dates + currency) so they can be adjusted (owner Andre
        // 2026-09-18: on create just customer + reference; the rest shows "if I edit the document").
        // The ISSUED date is fixed once set — read-only (owner: "should not be able to change the
        // issue date").
        node.querySelectorAll('[data-adv]').forEach(el => el.removeAttribute('hidden'));
        const ddEl = node.querySelector('[data-doc-date]');
        const ddInput = ddEl && ddEl.querySelector('.sleek-date-in');
        if(ddInput){ ddInput.readOnly = true; ddInput.tabIndex = -1; }
        const ddBtn = ddEl && ddEl.querySelector('.sleek-date-cal-btn');
        if(ddBtn) ddBtn.style.display = 'none';
        // the document keeps the currency it was made in — reopening it must not relabel its figures
        // with the contact's CURRENT currency (that only changes when a draft's party is changed)
        setCurrency((d.currency_id !== null && d.currency_id !== undefined && d.currency_id !== '') ? d.currency_id : ref.value.base_currency_id,
          { code: d.currency_code, symbol: d.currency_symbol });
        if(d.contact_id) partySel.set(String(d.contact_id));
        node.querySelector('[data-doc-date]').value = (d.document_date || todayIso()).slice(0, 10);
        if(d.due_date) node.querySelector('[data-due-date]').value = d.due_date.slice(0, 10);
        node.querySelector('[data-ref]').value = d.reference || '';
        node.querySelector('[data-notes]').value = d.notes || '';
        lines = docLines.map(l => ({ item_id: l.item_id || null, description: l.description || '',
          qty: l.qty, unit_price: l.unit_price, discount: l.discount || 0, tax_code_id: l.tax_code_id || null,
          account_id: l.account_id || null, unit: '' }));
        if(!lines.length) lines = [blankLine()];
        mountAttachments();
        // A posted/confirmed document is immutable (owner Andre 2026-09-18: "If invoice is posted …
        // you can't change anything"): lock the whole form and swap the draft actions for Send only.
        if((d.status || 'draft') !== 'draft'){
          posted = true;
          node.querySelector('.de-title').textContent = noun + (d.document_no ? ' · ' + d.document_no : '');
          node.querySelector('[data-editor]').classList.add('de-readonly');
          node.querySelector('[data-add-line]')?.setAttribute('hidden', '');
          node.querySelectorAll('[data-draft-only]').forEach(el => el.setAttribute('hidden', ''));
          node.querySelector('[data-send]').hidden = false;
          // Convert chain: e.g. an issued quote → Create Invoice. Hidden once already converted
          // (owner Andre: "can't invoice quote twice") — the backend also guards it.
          // Convert action lives top-right (owner Andre 2026-09-18: "move the create invoice button
          // to top right").
          const conv = CONVERT[typeCode];
          if(conv && !d.converted_to){
            const cb = html`<button type="button" class="btn btn-sm btn-primary">${conv.labelKey ? t(conv.labelKey, conv.label) : conv.label}</button>`.firstElementChild;
            cb.addEventListener('click', async () => {
              cb.disabled = true;
              try {
                const res = await api.post(base + '/documents/' + savedId + '/convert', { target_type: conv.type });
                navigate('/co/' + companyId + '/' + (KEY_FOR_TYPE[conv.type] || docKey) + '/' + res.id + '/edit');
              } catch(e2){ saveErr.value = (e2.data && e2.data.error) || t('doc.convert_failed', 'Could not convert'); cb.disabled = false; }
            });
            node.querySelector('[data-head-actions]').append(cb);
          }
        }
      } else {
        // New: prefill the party from ?contact= (from the contact detail's New ▾ dropdown).
        const pre = new URLSearchParams(location.search).get('contact');
        if(pre){ partySel.set(pre); selectParty(pre); }
        lines = [blankLine()];
        mountPendingAttachments();   // attach files before the first save
      }

      renderLines();
      coopWarn();                 // a draft loaded on a co-op's supplier (its party, date and lock are known now)
      // Clone / Repeat on an already-saved document (top-right, beside any Convert action).
      if(savedId) mountRowActions();
      // "Make recurring" toggle on a draft invoice/bill (hidden on posted docs via data-draft-only).
      if(!posted) mountRecurring();
      // A posted doc is fully locked — disable every control now that the lines exist.
      if(posted){
        node.querySelectorAll('[data-editor] input, [data-editor] textarea, [data-editor] .sleek-date-in')
          .forEach(el => { el.readOnly = true; el.disabled = true; });
      }
      node.querySelector('[data-save]').addEventListener('click', doSave);
      node.querySelector('[data-confirm]').addEventListener('click', doConfirm);
      node.querySelector('[data-send]').addEventListener('click', async () => {
        const btn = node.querySelector('[data-send]'); btn.disabled = true;
        try { await api.post(base + '/documents/' + savedId + '/send', {}); toast(t('doc.sent', 'Sent')); navigate('/co/' + companyId + '/' + listKey()); }
        catch(e){ saveErr.value = (e.data && e.data.error) || t('doc.send_failed', 'Could not send'); btn.disabled = false; }
      });
      node.querySelector('[data-add-line]').addEventListener('click', () => {
        lines.push(blankLine()); renderLines();
        // Jump straight into the new line's item search (owner Andre 2026-09-18).
        const rows = node.querySelectorAll('[data-lines] tr');
        rows[rows.length - 1]?.querySelector('.de-c-item .ss-input')?.focus();
      });

      loading.value = false;
      node.querySelector('[data-editor]').removeAttribute('hidden');
    } catch(e){
      console.error('[document_editor] boot failed', e);
      err.value = (e.data && e.data.error) || e.message || t('doc.load_failed', 'Could not load the editor');
      loading.value = false;
    }
  })();

  return tenantShell(companyId, `${docId ? 'Edit' : 'New'} ${noun}`, _frag);
}
